import React, { useState, useEffect, useMemo } from "react";
/* Components */
import {
    Accordion,
    AccordionSummary,
    AccordionDetails,
    Typography,
    MenuItem,
    Grid,
    TextField,
} from "@material-ui/core";
import ExpandMoreIcon from "@material-ui/icons/ExpandMore";
/* Form */
import { useForm, Controller } from "react-hook-form";
import useFormHandlers from "../useFormHandlers";
import { useSettingDispatchChange } from "../index";
import { yupResolver } from "@hookform/resolvers/yup";
/* State */
import { useEnqueueSnackbar } from "../../../../hooks/useEnqueueSnackbar";
/* Utilities */
import _ from "lodash";
import apiClient from "../../../../auth/apiClient";
import * as Yup from "yup";
/* Style */
import { useStyles } from "./LanguageSupport.styles";
import { useStyles as useRootStyles } from "../styles";
import ChipMultiSelect from "../../../Dropdowns/ChipMultiSelect";
/* Services */
import LocalizationService from "../../../../services/LocalizationService";

const LocalizationServices = new LocalizationService(apiClient);

const LanguageSettingsValidation = Yup.object().shape({
    defaultlanguage: Yup.number(),
    multilanguages: Yup.array(),
});

const formatOptions = (options) =>
    options?.map((option) => ({
        id: option.localeId,
        value: option.language,
    })) ?? [];

const sortOptionsAscending = (a, b) => {
    const nameA = a?.value?.toLowerCase() ?? "";
    const nameB = b?.value?.toLowerCase() ?? "";
    return nameA.localeCompare(nameB);
};

const sortOptions = (options) => [...options].sort(sortOptionsAscending);

const LanguageSupportSettingsForm = ({
    identifier,
    entityID,
    entityType,
    settingValues,
}) => {
    const classes = useStyles();
    const rootClasses = useRootStyles();
    const [languageSupportExpanded, setLanguageSupportExpanded] = useState(true);
    const [listOfLanguages, setListOfLanguages] = useState([]);
    const enqueueSnackbar = useEnqueueSnackbar();

    const fetchLanguages = async () => {
        try {
            const result = await LocalizationServices.getAllLanguages();
            console.log("Fetched languages:", result);
            if (result?.data?.length > 0) {
                setListOfLanguages(sortOptions(formatOptions(result.data)))
            } else {
                enqueueSnackbar("Unable to get languages", {
                    variant: "error",
                    tag: "LocalizationServices",
                });
            }
        } catch (error) {
            enqueueSnackbar("Unable to get languages", {
                variant: "error",
                tag: "LocalizationServices",
            });
        }
    }

    useEffect(() => {
        fetchLanguages();
    }, []);

    // Compute default values
    function getDefaultValues() {
        return {
            defaultlanguage: settingValues?.defaultlanguage ? Number(settingValues.defaultlanguage) : 0,
            multilanguages:
                typeof settingValues?.multilanguages === "string" && settingValues?.multilanguages
                    ? JSON.parse(settingValues?.multilanguages)
                    : settingValues?.multilanguages || [],
        };
    }

    //Initialize form
    const { control, setValue, watch } = useForm({
        resolver: yupResolver(LanguageSettingsValidation),
        defaultValues: getDefaultValues(),
    });

    useEffect(() => {
        if (listOfLanguages.length === 0 || !settingValues) return;
        const currentDefault = watch("defaultlanguage");
        const isValid = listOfLanguages.some(
            l => l.id === currentDefault
        );
        if (!isValid) {
            const fallbackLang = listOfLanguages.find(l => l.value === "English") || listOfLanguages[0];
            if (fallbackLang) {
                setDefaultLanguage(fallbackLang.id);
            }
        }
    }, [listOfLanguages, settingValues]);

    const { dispatchSetting } = useSettingDispatchChange();
    const { canRenderField } = useFormHandlers(
        setValue,
        settingValues,
        entityID,
        entityType
    );

    const handleAccordionToggle = () => setLanguageSupportExpanded((prev) => !prev);

    const setDefaultLanguage = async (value) => {
        const defaultLangSuccess = await dispatchSetting(
            entityID,
            "cultures.defaultlanguage",
            value
        );
        if (!defaultLangSuccess) {
            enqueueSnackbar("Unable to set Default Language", {
                variant: "error",
                tag: "defaultLanguage",
            });
            return false;
        }
        setValue("defaultlanguage", value);
        return true;
    };

    const handleDefaultLanguageSelect = async (e) => {
        const targetValue = e.target.value;
        const success = await setDefaultLanguage(targetValue);
        if (!success) return;
        // Remove the selected default language from multilanguages if present
        const currentMulti = watch("multilanguages") || [];
        const filteredMulti = currentMulti.filter(id => id !== targetValue);
        if (filteredMulti.length !== currentMulti.length) {
            // Update multilanguages in backend
            const multiLangSuccess = await dispatchSetting(
                entityID,
                "cultures.multilanguages",
                JSON.stringify(filteredMulti)
            );
            if (!multiLangSuccess) {
                enqueueSnackbar("Unable to update multi languages after default language change", {
                    variant: "error",
                    tag: "multilanguages",
                });
                return;
            }
            setValue("multilanguages", filteredMulti);
        }
    };

    const handleLanguageChange = async (selectedLanguages, prevLanguages) => {
        const ids = selectedLanguages?.map((option) => option.id) ?? [];
        let setOptions = false;
        if (prevLanguages) {
            setValue("multilanguages", ids);
            setOptions = true;
        }
        if (
            !(await dispatchSetting(
                entityID,
                "cultures.multilanguages",
                JSON.stringify(ids)
            ))
        ) {
            enqueueSnackbar("Unable to update selected multi languages", {
                variant: "error",
                tag: "multilanguages",
            });
            if (prevLanguages) {
                const prevOptionIDs = prevLanguages?.map((option) => option.id) ?? [];
                setValue("multilanguages", prevOptionIDs);
            }
        } else if (!setOptions) {
            setValue("multilanguages", ids);
        }
    };

    // Memoize available multi-language options
    const defaultLang = watch("defaultlanguage");
    const availableMultiLangs = useMemo(
        () =>
            listOfLanguages.filter(
                lang => lang.id !== defaultLang
            ),
        [listOfLanguages, defaultLang]
    );

    const MultiLang = watch("multilanguages");
    const selectedMultiLanguages = useMemo(() => {
        return (MultiLang ?? [])
            .map(id => availableMultiLangs.find(lang => lang.id === id))
            .filter(lang => !!lang);
    }, [MultiLang, availableMultiLangs]);

    return (
        <Grid className={rootClasses.panelRoot} data-testid={identifier} container>
            <Accordion
                data-tag="language-support"
                expanded={languageSupportExpanded}
                TransitionProps={{ unmountOnExit: true }}
                className={rootClasses.fullWidthAccordion}
            >
                <AccordionSummary
                    expandIcon={<ExpandMoreIcon />}
                    onClick={handleAccordionToggle}
                >
                    <Typography className={rootClasses.heading}>
                        Language Support
                    </Typography>
                    <Typography className={rootClasses.secondaryHeading}></Typography>
                </AccordionSummary>
                <AccordionDetails>
                    {canRenderField("languagesupport") && (
                        <Grid container spacing={2}>
                            <Grid
                                item
                                xs={12}
                                sm={4}
                                data-tag="language-support"
                                className={classes.languageSupportSettingContainer}
                            >
                                <Controller
                                    name="defaultlanguage"
                                    control={control}
                                    render={({ field }) => (
                                        <TextField
                                            {...field}
                                            className={classes.languageSupportSelector}
                                            data-id="defaultlanguage"
                                            id="defaultlanguage"
                                            label={<Typography>Default Language</Typography>}
                                            select
                                            variant="outlined"
                                            onChange={handleDefaultLanguageSelect}
                                            inputProps={{
                                                "data-testid": "defaultlanguage-select",
                                            }}
                                        >
                                            {listOfLanguages.map((item) => (
                                                <MenuItem key={item.id} value={item.id}>
                                                    {item.value}
                                                </MenuItem>
                                            ))}
                                        </TextField>
                                    )}
                                />
                            </Grid>
                            <Grid item xs={12} className={classes.multiLanguageContainer}>
                                <Controller
                                    name="multilanguages"
                                    control={control}
                                    render={({ field }) => (
                                        <ChipMultiSelect
                                            className={classes.multiLanguageSelector}
                                            identifier="multilanguages-selector"
                                            label="Languages"
                                            options={availableMultiLangs}
                                            onChange={handleLanguageChange}
                                            selected={selectedMultiLanguages}
                                        />
                                    )}
                                />
                            </Grid>
                        </Grid>
                    )}
                </AccordionDetails>
            </Accordion>
        </Grid>
    );
};

export default LanguageSupportSettingsForm;